import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, BarChart3, FileText, Wallet,
  ArrowUpCircle, ArrowDownCircle, Clock, RefreshCw,
  DollarSign, Bot, Radio, Plus, X,
  AlertTriangle, Shield, Trash2, Star, Loader2,
  Download, Send, Eye, Mail,
} from 'lucide-react';
import { planService } from '@/services/plan.service';
import { creditsService, CreditTransaction } from '@/services/credits.service';
import { couponService, type ActiveCoupon } from '@/services/coupon.service';
import { paymentService, type PaymentMethodInfo } from '@/services/payment.service';
import { AddCardForm } from '@/components/billing/AddCardForm';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';
import type { PlanInfo, Invoice, UsageRecord, BillingCycle } from '@/types';

const tabs = [
  { id: 'plan', label: 'Plan', icon: CreditCard },
  { id: 'usage', label: 'Usage', icon: BarChart3 },
  { id: 'invoices', label: 'Invoices', icon: FileText },
  { id: 'credits', label: 'Credits', icon: Wallet },
  { id: 'payment', label: 'Payment Methods', icon: Shield },
];

const creditTypeConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  topup: { label: 'Top Up', color: 'text-green-600 bg-green-50', icon: ArrowUpCircle },
  reserve: { label: 'Reserve', color: 'text-yellow-600 bg-yellow-50', icon: Clock },
  usage: { label: 'Usage', color: 'text-red-600 bg-red-50', icon: ArrowDownCircle },
  settle: { label: 'Settle', color: 'text-blue-600 bg-blue-50', icon: RefreshCw },
  adjustment: { label: 'Adjustment', color: 'text-purple-600 bg-purple-50', icon: DollarSign },
  refund: { label: 'Refund', color: 'text-green-600 bg-green-50', icon: ArrowUpCircle },
};

const PLAN_FEATURES = [
  { name: 'AI Agents', free: '3 max', payg: '3 free + $5/mo extra' },
  { name: 'Channels', free: '2 max', payg: '2 free + $5/mo extra' },
  { name: 'Credits', free: '5 on signup', payg: '5 on signup' },
  { name: 'Knowledge Base', free: 'Included', payg: 'Included' },
  { name: 'Support Requests', free: null, payg: '$20/request' },
  { name: 'Training Sessions', free: null, payg: '$40/session' },
  { name: 'Simulation Runs', free: null, payg: '$2/run' },
];

export const BillingPage: React.FC = () => {
  const { setOrganization } = useAuthStore();
  const [activeTab, setActiveTab] = useState('plan');

  // Plan state
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);

  // Usage state
  const [cycle, setCycle] = useState<BillingCycle | null>(null);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);

  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Credits state
  const [balance, setBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [txnPage, setTxnPage] = useState(0);
  const txnLimit = 20;

  // Coupon state
  const [activeCoupon, setActiveCoupon] = useState<ActiveCoupon | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodInfo[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [paymentActionLoading, setPaymentActionLoading] = useState<string | null>(null);

  // Buy credits state
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [buyAmount, setBuyAmount] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  const [creditDiscount, setCreditDiscount] = useState<{
    applicable: boolean;
    discountAmount: number;
    chargeAmount: number;
    creditAmount: number;
    couponCode?: string;
    reason?: string;
  } | null>(null);

  // Pay invoice state
  const [payInvoiceLoading, setPayInvoiceLoading] = useState<string | null>(null);

  // Send email state
  const [sendEmailLoading, setSendEmailLoading] = useState<string | null>(null);
  const [sendEmailInvoiceId, setSendEmailInvoiceId] = useState<string | null>(null);
  const [sendEmailAddress, setSendEmailAddress] = useState('');

  // Fetch plan info
  const fetchPlan = useCallback(async () => {
    setPlanLoading(true);
    try {
      const data = await planService.getPlan();
      setPlanInfo(data);
    } catch {
      toast.error('Failed to load plan info');
    } finally {
      setPlanLoading(false);
    }
  }, []);

  // Fetch usage
  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const data = await planService.getUsage();
      setCycle(data.cycle);
      setUsageRecords(data.records);
    } catch {
      toast.error('Failed to load usage data');
    } finally {
      setUsageLoading(false);
    }
  }, []);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const data = await planService.getInvoices();
      setInvoices(data);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  // Fetch credits
  const fetchCredits = useCallback(async () => {
    setCreditsLoading(true);
    try {
      const [balanceData, txnData] = await Promise.all([
        creditsService.getBalance(),
        creditsService.getTransactions(txnLimit, txnPage * txnLimit),
      ]);
      setBalance(balanceData.balance);
      setTransactions(txnData.transactions);
      setTxnTotal(txnData.total);
    } catch {
      toast.error('Failed to load credit data');
    } finally {
      setCreditsLoading(false);
    }
  }, [txnPage]);

  // Fetch active coupon
  const fetchActiveCoupon = useCallback(async () => {
    try {
      const coupon = await couponService.getActive();
      setActiveCoupon(coupon);
    } catch {
      // Silently fail — coupon display is non-critical
    }
  }, []);

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    setPaymentLoading(true);
    try {
      const methods = await paymentService.listMethods();
      setPaymentMethods(methods);
    } catch {
      toast.error('Failed to load payment methods');
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'plan') {
      fetchPlan();
      fetchActiveCoupon();
    } else if (activeTab === 'usage') fetchUsage();
    else if (activeTab === 'invoices') fetchInvoices();
    else if (activeTab === 'credits') fetchCredits();
    else if (activeTab === 'payment') fetchPaymentMethods();
  }, [activeTab, fetchPlan, fetchActiveCoupon, fetchUsage, fetchInvoices, fetchCredits, fetchPaymentMethods]);

  useEffect(() => {
    const amt = parseFloat(buyAmount);
    if (!amt || amt < 5 || amt > 500 || !showBuyCredits) {
      setCreditDiscount(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await paymentService.previewCreditDiscount(amt);
        setCreditDiscount(result);
      } catch {
        setCreditDiscount(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [buyAmount, showBuyCredits]);

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      // Check if payment method exists
      const hasMethod = await paymentService.hasMethod();
      if (!hasMethod) {
        toast.error('Please add a payment method first');
        setActiveTab('payment');
        setUpgradeLoading(false);
        return;
      }
      const result = await planService.upgrade();
      setOrganization(result);
      toast.success('Upgraded to Pay As You Go');
      fetchPlan();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to upgrade plan');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleDowngrade = async () => {
    setUpgradeLoading(true);
    try {
      const result = await planService.downgrade();
      setOrganization(result);
      setShowDowngradeConfirm(false);
      toast.success('Downgraded to Free plan');
      fetchPlan();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to downgrade');
    } finally {
      setUpgradeLoading(false);
    }
  };


  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    setCouponLoading(true);
    try {
      await couponService.redeem(couponCode);
      toast.success('Coupon applied successfully');
      setCouponCode('');
      fetchActiveCoupon();
    } catch {
      toast.error('Failed to apply coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      await couponService.removeActive();
      setActiveCoupon(null);
      toast.success('Coupon removed');
    } catch {
      toast.error('Failed to remove coupon');
    }
  };

  const handleSetDefault = async (methodId: string) => {
    setPaymentActionLoading(methodId);
    try {
      await paymentService.setDefault(methodId);
      toast.success('Default payment method updated');
      fetchPaymentMethods();
    } catch {
      // Error toast handled by API interceptor
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const handleRemoveMethod = async (methodId: string) => {
    setPaymentActionLoading(methodId);
    try {
      await paymentService.removeMethod(methodId);
      toast.success('Payment method removed');
      fetchPaymentMethods();
    } catch {
      // Error toast handled by API interceptor
    } finally {
      setPaymentActionLoading(null);
    }
  };

  const handlePayInvoice = async (invoiceId: string) => {
    setPayInvoiceLoading(invoiceId);
    try {
      const result = await paymentService.payInvoice(invoiceId);
      if (result.status === 'succeeded') {
        toast.success('Invoice paid successfully');
      } else {
        toast.success(`Payment processing (${result.status})`);
      }
      fetchInvoices();
    } catch {
      // Error toast handled by API interceptor
    } finally {
      setPayInvoiceLoading(null);
    }
  };

  const handleBuyCredits = async () => {
    const amount = parseFloat(buyAmount);
    if (!amount || amount < 5 || amount > 500) {
      toast.error('Enter an amount between $5 and $500');
      return;
    }
    setBuyLoading(true);
    try {
      const result = await paymentService.purchaseCredits(amount);
      const discountText = result.discount
        ? ` (saved $${result.discount.amount.toFixed(2)} with coupon ${result.discount.couponCode})`
        : '';
      toast.success(`${result.credits} credits purchased${discountText}`);
      setShowBuyCredits(false);
      setBuyAmount('');
      setCreditDiscount(null);
      fetchCredits();
    } catch {
      // Error toast handled by API interceptor
    } finally {
      setBuyLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string, _invoiceNumber: string) => {
    try {
      const html = await planService.getInvoiceHtml(invoiceId);
      // Open in new window for printing/saving as PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch {
      // Error toast handled by API interceptor
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    if (!sendEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setSendEmailLoading(invoiceId);
    try {
      await planService.sendInvoice(invoiceId, sendEmailAddress);
      toast.success('Invoice sent successfully');
      setSendEmailInvoiceId(null);
      setSendEmailAddress('');
    } catch {
      // Error toast handled by API interceptor
    } finally {
      setSendEmailLoading(null);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const txnTotalPages = Math.ceil(txnTotal / txnLimit);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your plan, usage, invoices, and credits</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== PLAN TAB ===== */}
      {activeTab === 'plan' && (
        <div className="space-y-6">
          {planLoading ? (
            <div className="p-12 text-center text-gray-500">Loading...</div>
          ) : planInfo ? (
            <>
              {/* Current Plan Card */}
              <div className={`rounded-xl p-6 text-white ${
                planInfo.plan === 'pay_as_you_go'
                  ? 'bg-blue-500 border-2 border-[#1a1a2e]'
                  : 'bg-gray-600 border-2 border-[#1a1a2e]'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium opacity-80">Current Plan</p>
                    <p className="text-3xl font-bold mt-1">
                      {planInfo.plan === 'pay_as_you_go' ? 'Pay As You Go' : 'Free'}
                    </p>
                    <p className="text-sm opacity-70 mt-1">
                      {planInfo.plan === 'pay_as_you_go' ? '$10/month — prorated daily' : 'No monthly charge'}
                    </p>
                  </div>
                  {planInfo.plan === 'free' ? (
                    <button
                      onClick={handleUpgrade}
                      disabled={upgradeLoading}
                      className="px-5 py-2.5 bg-white text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 disabled:opacity-50 transition-colors"
                    >
                      {upgradeLoading ? 'Upgrading...' : 'Upgrade to Pay As You Go'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowDowngradeConfirm(true)}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
                    >
                      Downgrade to Free
                    </button>
                  )}
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border-2 border-[#1a1a2e] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Agents</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Used</span>
                      <span className="font-medium text-gray-900">{planInfo.usage.agents.used}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Free included</span>
                      <span className="font-medium text-gray-900">{planInfo.usage.agents.free}</span>
                    </div>
                    {planInfo.usage.agents.extra > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Extra (billed)</span>
                        <span className="font-medium text-amber-600">{planInfo.usage.agents.extra}</span>
                      </div>
                    )}
                    <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (planInfo.usage.agents.used / Math.max(1, planInfo.usage.agents.free)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-[#1a1a2e] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Radio className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Channels</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Used</span>
                      <span className="font-medium text-gray-900">{planInfo.usage.channels.used}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Free included</span>
                      <span className="font-medium text-gray-900">{planInfo.usage.channels.free}</span>
                    </div>
                    {planInfo.usage.channels.extra > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Extra (billed)</span>
                        <span className="font-medium text-amber-600">{planInfo.usage.channels.extra}</span>
                      </div>
                    )}
                    <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (planInfo.usage.channels.used / Math.max(1, planInfo.usage.channels.free)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Comparison */}
              <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#1a1a2e]">
                  <h3 className="text-lg font-semibold text-gray-900">Plan Comparison</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a2e]">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Free</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pay As You Go</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {PLAN_FEATURES.map((f) => (
                      <tr key={f.name} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{f.name}</td>
                        <td className="px-6 py-3 text-sm text-center">
                          {f.free ? (
                            <span className="text-gray-700">{f.free}</span>
                          ) : (
                            <X className="w-4 h-4 text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm text-center">
                          <span className="text-gray-700">{f.payg}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Coupon Section */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Coupon</h3>
                {activeCoupon ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-green-600 font-medium">{activeCoupon.coupon_code}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        {activeCoupon.coupon_discount_type === 'percentage'
                          ? `${activeCoupon.coupon_discount_value}% off`
                          : activeCoupon.coupon_discount_type === 'free_period'
                            ? `${activeCoupon.cycles_remaining || activeCoupon.coupon_discount_value} free months remaining`
                            : `$${activeCoupon.coupon_discount_value} off`}
                      </span>
                    </div>
                    <button onClick={handleRemoveCoupon} className="text-red-500 text-sm hover:underline">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={!couponCode || couponLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
                    >
                      {couponLoading ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {/* Downgrade Confirmation Modal */}
          {showDowngradeConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDowngradeConfirm(false)}>
              <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Downgrade to Free?</h3>
                </div>
                <p className="text-sm text-gray-600">
                  You must have 3 or fewer agents and 2 or fewer channels to downgrade.
                  Extra agents and channels will need to be removed first.
                  Support, training, and simulation features will no longer be available.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDowngradeConfirm(false)}
                    className="px-4 py-2 border-2 border-[#1a1a2e] text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDowngrade}
                    disabled={upgradeLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    {upgradeLoading ? 'Downgrading...' : 'Confirm Downgrade'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== USAGE TAB ===== */}
      {activeTab === 'usage' && (
        <div className="space-y-6">
          {usageLoading ? (
            <div className="p-12 text-center text-gray-500">Loading...</div>
          ) : (
            <>
              {cycle && (
                <div className="bg-blue-500 border-2 border-[#1a1a2e] rounded-xl p-6 text-white">
                  <p className="text-blue-100 text-sm font-medium">Current Billing Cycle</p>
                  <p className="text-xl font-bold mt-1">
                    {formatDate(cycle.period_start)} — {formatDate(cycle.period_end)}
                  </p>
                  <p className="text-blue-200 text-xs mt-1">Status: {cycle.status}</p>
                </div>
              )}

              <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#1a1a2e]">
                  <h3 className="text-lg font-semibold text-gray-900">Usage Records</h3>
                  <p className="text-sm text-gray-500">{usageRecords.length} records this cycle</p>
                </div>
                {usageRecords.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">No usage records this cycle</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1a1a2e]">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {usageRecords.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">
                            {r.module_name || r.module_slug || r.module_id}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-600">
                            {r.reference_label || '—'}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500">
                            {formatDate(r.started_at)}
                          </td>
                          <td className="px-6 py-3">
                            {r.ended_at ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
                                Ended {formatDate(r.ended_at)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-green-700 bg-green-50">
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== INVOICES TAB ===== */}
      {activeTab === 'invoices' && (
        <div className="space-y-6">
          <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#1a1a2e]">
              <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
            </div>
            {invoiceLoading ? (
              <div className="p-12 text-center text-gray-500">Loading...</div>
            ) : invoices.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No invoices yet</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1a1a2e]">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{inv.invoice_number}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{formatDate(inv.created_at)}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{formatDate(inv.due_date)}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          inv.status === 'paid' ? 'text-green-700 bg-green-50' :
                          inv.status === 'processing' ? 'text-blue-700 bg-blue-50' :
                          inv.status === 'void' ? 'text-red-700 bg-red-50' :
                          'text-yellow-700 bg-yellow-50'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">${Number(inv.total).toFixed(2)}</td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {inv.status === 'pending' && (
                            <button
                              onClick={() => handlePayInvoice(inv.id)}
                              disabled={payInvoiceLoading === inv.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                              {payInvoiceLoading === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                              Pay
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedInvoice(inv)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => handleDownloadInvoice(inv.id, inv.invoice_number)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => { setSendEmailInvoiceId(inv.id); setSendEmailAddress(''); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100"
                          >
                            <Send className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Send Email Popup */}
          {sendEmailInvoiceId && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSendEmailInvoiceId(null)}>
              <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Send Invoice
                  </h3>
                  <button onClick={() => setSendEmailInvoiceId(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Send invoice #{invoices.find(i => i.id === sendEmailInvoiceId)?.invoice_number} to an email address.</p>
                <input
                  type="email"
                  value={sendEmailAddress}
                  onChange={(e) => setSendEmailAddress(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-4"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSendInvoice(sendEmailInvoiceId)}
                    disabled={sendEmailLoading === sendEmailInvoiceId}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] text-white rounded-lg hover:bg-[#2d2d4e] text-sm font-medium disabled:opacity-50"
                  >
                    {sendEmailLoading === sendEmailInvoiceId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {sendEmailLoading === sendEmailInvoiceId ? 'Sending...' : 'Send'}
                  </button>
                  <button
                    onClick={() => setSendEmailInvoiceId(null)}
                    className="px-4 py-2 border-2 border-[#1a1a2e] text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invoice Detail Modal */}
          {selectedInvoice && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
              <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="bg-[#1a1a2e] text-white p-6 rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">Invoice {selectedInvoice.invoice_number}</h3>
                      <p className="text-sm text-gray-300 mt-1">ChatCup AI</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownloadInvoice(selectedInvoice.id, selectedInvoice.invoice_number)}
                        className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Download / Print"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => { setSendEmailInvoiceId(selectedInvoice.id); setSendEmailAddress(''); }}
                        className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Send via Email"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                      <button onClick={() => setSelectedInvoice(null)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Status + Dates */}
                  <div className="flex flex-wrap gap-6 items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold mt-1 ${
                        selectedInvoice.status === 'paid' ? 'text-green-700 bg-green-50' :
                        selectedInvoice.status === 'processing' ? 'text-blue-700 bg-blue-50' :
                        selectedInvoice.status === 'void' ? 'text-red-700 bg-red-50' :
                        'text-yellow-700 bg-yellow-50'
                      }`}>{selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}</span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(selectedInvoice.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Due Date</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(selectedInvoice.due_date)}</p>
                    </div>
                    {selectedInvoice.paid_at && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Paid</p>
                        <p className="text-sm font-medium text-green-600 mt-1">{formatDate(selectedInvoice.paid_at)}</p>
                      </div>
                    )}
                  </div>

                  {/* Payment Info */}
                  {selectedInvoice.payment_provider && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Payment Details</p>
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span>Paid via {selectedInvoice.payment_provider}</span>
                        {selectedInvoice.payment_intent_id && (
                          <span className="text-xs text-gray-400 font-mono">({selectedInvoice.payment_intent_id.slice(0, 20)}...)</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Line Items */}
                  <div>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-[#1a1a2e]">
                          <th className="py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                          <th className="py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Qty</th>
                          <th className="py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Rate</th>
                          <th className="py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedInvoice.line_items.map((li, i) => (
                          <tr key={i}>
                            <td className="py-3 text-sm text-gray-900">{li.description}</td>
                            <td className="py-3 text-sm text-gray-500 text-right">{li.days ? `${li.days} days` : li.count ? `${li.count}x` : '—'}</td>
                            <td className="py-3 text-sm text-gray-500 text-right">${Number(li.price).toFixed(2)}</td>
                            <td className="py-3 text-sm font-semibold text-gray-900 text-right">${Number(li.total).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="border-t-2 border-[#1a1a2e] pt-4">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subtotal</span>
                          <span className="font-medium text-gray-900">${Number(selectedInvoice.subtotal).toFixed(2)}</span>
                        </div>
                        {selectedInvoice.total !== selectedInvoice.subtotal && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Discount</span>
                            <span className="font-medium text-green-600">-${(Number(selectedInvoice.subtotal) - Number(selectedInvoice.total)).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg border-t border-gray-200 pt-2">
                          <span className="font-bold text-gray-900">Total</span>
                          <span className="font-bold text-gray-900">${Number(selectedInvoice.total).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {selectedInvoice.status === 'pending' && (
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => { handlePayInvoice(selectedInvoice.id); setSelectedInvoice(null); }}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay Now
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== CREDITS TAB ===== */}
      {activeTab === 'credits' && (
        <div className="space-y-6">
          {/* Balance Card */}
          <div className="bg-blue-500 border-2 border-[#1a1a2e] rounded-xl p-6 text-white flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Available Balance</p>
              <p className="text-4xl font-bold mt-1">${balance.toFixed(4)}</p>
              <p className="text-blue-200 text-xs mt-2">USD — deducted per AI interaction</p>
            </div>
            <button
              onClick={() => setShowBuyCredits(!showBuyCredits)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Buy Credits
            </button>
          </div>

          {/* Buy Credits Form */}
          {showBuyCredits && (
            <div className="bg-white border-2 border-[#1a1a2e] rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Buy Credits with Card</h3>
              <p className="text-sm text-gray-500">Purchase credits using your saved payment method. $1 = 1 credit. Min $5, max $500.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (USD)</label>
                <input
                  type="number"
                  step="1"
                  min="5"
                  max="500"
                  value={buyAmount}
                  onChange={(e) => setBuyAmount(e.target.value)}
                  placeholder="10"
                  className="w-full max-w-xs border-2 border-[#1a1a2e] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
                />
              </div>
              {creditDiscount?.applicable && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Credits you'll receive</span>
                    <span className="font-medium text-gray-900">{creditDiscount.creditAmount} credits</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Original price</span>
                    <span className="text-gray-500 line-through">${creditDiscount.creditAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Coupon ({creditDiscount.couponCode})</span>
                    <span className="font-medium text-green-600">-${creditDiscount.discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-green-200 pt-1 mt-1">
                    <span className="font-semibold text-gray-900">You pay</span>
                    <span className="font-bold text-gray-900">${creditDiscount.chargeAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}
              {creditDiscount && !creditDiscount.applicable && creditDiscount.reason && (
                <p className="text-xs text-amber-600">{creditDiscount.reason}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleBuyCredits}
                  disabled={buyLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] text-white rounded-lg hover:bg-[#2d2d4e] text-sm font-medium disabled:opacity-50"
                >
                  {buyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {buyLoading ? 'Processing...' : creditDiscount?.applicable ? `Purchase ($${creditDiscount.chargeAmount.toFixed(2)})` : 'Purchase'}
                </button>
                <button
                  onClick={() => { setShowBuyCredits(false); setCreditDiscount(null); }}
                  className="px-4 py-2 border-2 border-[#1a1a2e] text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Transaction History */}
          <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#1a1a2e]">
              <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
              <p className="text-sm text-gray-500">{txnTotal} total transactions</p>
            </div>
            {creditsLoading ? (
              <div className="p-12 text-center text-gray-500">Loading...</div>
            ) : transactions.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No transactions yet</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1a1a2e]">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((txn) => {
                    const config = creditTypeConfig[txn.type] || creditTypeConfig.adjustment;
                    const Icon = config.icon;
                    return (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {config.label}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-700 max-w-xs truncate">{txn.description || '—'}</td>
                        <td className={`px-6 py-3 text-sm font-semibold text-right ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Number(txn.amount) >= 0 ? '+' : ''}{Number(txn.amount).toFixed(6)}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500 text-right">${Number(txn.running_balance).toFixed(4)}</td>
                        <td className="px-6 py-3 text-xs text-gray-400 text-right">
                          {new Date(txn.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {txnTotalPages > 1 && (
              <div className="px-6 py-3 border-t border-[#1a1a2e] flex items-center justify-between">
                <button
                  onClick={() => setTxnPage(Math.max(0, txnPage - 1))}
                  disabled={txnPage === 0}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">Page {txnPage + 1} of {txnTotalPages}</span>
                <button
                  onClick={() => setTxnPage(Math.min(txnTotalPages - 1, txnPage + 1))}
                  disabled={txnPage >= txnTotalPages - 1}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== PAYMENT METHODS TAB ===== */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          {/* Add Card */}
          {showAddCard ? (
            <AddCardForm
              onSuccess={(method) => {
                setShowAddCard(false);
                setPaymentMethods((prev) => [...prev, method]);
                fetchPaymentMethods();
              }}
              onCancel={() => setShowAddCard(false)}
            />
          ) : (
            <button
              onClick={() => setShowAddCard(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] text-white rounded-lg hover:bg-[#2d2d4e] text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Payment Method
            </button>
          )}

          {/* Saved Cards */}
          <div className="bg-white border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#1a1a2e]">
              <h3 className="text-lg font-semibold text-gray-900">Saved Payment Methods</h3>
              <p className="text-sm text-gray-500">{paymentMethods.length} method{paymentMethods.length !== 1 ? 's' : ''} on file</p>
            </div>
            {paymentLoading ? (
              <div className="p-12 text-center text-gray-500">Loading...</div>
            ) : paymentMethods.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>No payment methods saved</p>
                <p className="text-xs mt-1">Add a card to pay invoices and buy credits</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {paymentMethods.map((pm) => (
                  <div key={pm.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {pm.brand || 'Card'}
                          </span>
                          <span className="text-sm text-gray-500">
                            ending in {pm.last_four || '****'}
                          </span>
                          {pm.is_default && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-blue-700 bg-blue-50">
                              <Star className="w-3 h-3" />
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          Expires {pm.exp_month}/{pm.exp_year}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!pm.is_default && (
                        <button
                          onClick={() => handleSetDefault(pm.id)}
                          disabled={paymentActionLoading === pm.id}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveMethod(pm.id)}
                        disabled={paymentActionLoading === pm.id}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Remove"
                      >
                        {paymentActionLoading === pm.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
